/**
 * Mana Locals Admin — M83 agreements, SUBJECT-facing wire contracts.
 *
 * CONTRACT SOURCE: `contracts/agreements.register.md` (register v1.4) and the API's
 * `src/domains/m83-agreements/contracts/agreements.schemas.ts`. The API schema is the
 * source of truth; this file mirrors it so the client parses the SAME shape the server
 * emits. Where the UI handoff document (`docs/m83-agreements-ui-handoff.md`) simplifies a
 * payload, the API schema wins and the difference is noted inline.
 *
 * These are the routes a VENDOR or CUSTOMER calls about ITSELF. They require no platform
 * authority and no platform enrolment. The platform-operator routes live in
 * `agreements-platform.ts` and must never be called from a subject surface.
 *
 * Nothing in this file is an authorization statement. Zod validates SHAPE only.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common';

/** A document key: lowercase, digits and underscores. NO hyphens. */
export const AgreementDocumentKeySchema = z.string().regex(/^[a-z][a-z0-9_]{1,63}$/);

/** A locale tag, e.g. `en` or `en-IN`. */
export const AgreementLocaleSchema = z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/);

/** A sha-256 hex digest as the API emits it. */
export const AgreementSha256Schema = z.string().regex(/^[0-9a-f]{64}$/);

/* --------------------------------------------------------------------------------------------
 * Enums — mirrored from the API, NOT from the handoff's shorthand
 * ------------------------------------------------------------------------------------------ */

/**
 * The audience a document addresses.
 *
 * The API accepts `both`; the handoff document writes only `tenant`/`customer`. A requirement
 * is always created for ONE audience (`tenant` or `customer`), so `both` appears only on a
 * document.
 */
export const AgreementAudienceSchema = z.enum(['tenant', 'customer', 'both']);

/**
 * What the subject must do.
 *
 * The API's vocabulary is `acknowledgment` | `consent` | `signature`. The handoff document
 * writes `acceptance` as shorthand for the acknowledgment/consent pair; the wire value is
 * never `acceptance`. Acknowledgment and consent are DISTINCT and must stay separate in the
 * UI: an acknowledgment is a read-and-confirm, a consent is an affirmative permission.
 */
export const AgreementRequiredActionSchema = z.enum(['acknowledgment', 'consent', 'signature']);

export const AgreementDocumentStatusSchema = z.enum(['draft', 'published', 'withdrawn']);

export const AgreementExecutionStatusSchema = z.enum([
  'pending',
  'completed',
  'declined',
  'expired',
]);

export const AgreementSubjectKindSchema = z.enum(['tenant', 'customer']);

export const AgreementSigningMethodSchema = z.enum(['drawn', 'uploaded', 'checkbox']);

/**
 * The assurance a signature execution must achieve.
 *
 * `session_reattested` accepts a fresh re-proof of session possession; `fresh_strong` REQUIRES a
 * server-recorded WebAuthn step-up (`aal2`) within the freshness window. A session reattestation
 * is NEVER silently accepted in place of a `fresh_strong` requirement.
 */
export const SigningVerificationPolicySchema = z.enum(['session_reattested', 'fresh_strong']);

export const AgreementLocationStateSchema = z.enum(['granted', 'denied', 'unavailable']);

export const AgreementExecutionEventTypeSchema = z.enum([
  'session_created',
  'signature_captured',
  'pdf_generated',
  'completed',
  'declined',
  'expired',
  'evidence_recorded',
]);

/**
 * The recovery path offered when a signer cannot complete a `fresh_strong` step-up.
 *
 * `enrol_authenticator` — the signer has no enrolled authenticator and must enrol one.
 * `contact_administrator` — the deployment has an operator-mediated recovery.
 *
 * A recovery path NEVER relaxes the signing requirement; it only says what to do next.
 */
export const StepUpRecoveryPathSchema = z.enum(['enrol_authenticator', 'contact_administrator']);

/* --------------------------------------------------------------------------------------------
 * Request bodies
 * ------------------------------------------------------------------------------------------ */

/** `POST /agreements/executions/{executionId}/acceptance` */
export const AcceptAgreementRequestSchema = z.object({
  versionId: UuidSchema,
  subjectKind: AgreementSubjectKindSchema,
});

/** `POST /agreements/executions/{executionId}/signing-sessions` */
export const OpenSigningSessionRequestSchema = z.object({
  signingMethod: z.enum(['drawn', 'uploaded']),
  authorityDeclared: z.literal(true),
});

/**
 * `POST /agreements/executions/{executionId}/signature-upload`
 *
 * The client states the challenge of the signing session it is completing, the signing method,
 * and the image format and size it intends to upload. The SERVER generates the object key and
 * returns a short-lived presigned URL; the client uploads the bytes directly to that URL. The
 * client never chooses the object key.
 */
export const IssueSignatureUploadRequestSchema = z.object({
  challenge: z.string().min(1).max(256),
  signingMethod: z.enum(['drawn', 'uploaded']),
  signatureMimeType: z.enum(['image/png', 'image/jpeg']),
  signatureByteSize: z.number().int().positive().max(2 * 1024 * 1024),
});

/** `POST /agreements/executions/{executionId}/signing-verification` */
export const VerifySigningSessionRequestSchema = z.object({
  challenge: z.string().min(1).max(256),
});

/** `POST /agreements/executions/{executionId}/step-up/registration` and `.../authentication` */
export const BeginStepUpRequestSchema = z.object({
  challenge: z.string().min(1).max(256),
});

/**
 * `POST .../step-up/registration/complete` and `.../authentication/complete`.
 *
 * `response` is the RAW `PublicKeyCredential` JSON the browser produced; the platform verifier
 * validates it against the challenge it issued. It is typed as an opaque record because the
 * agreement module does not own the WebAuthn option shape — the platform's verifier does.
 */
export const FinishStepUpRequestSchema = z.object({
  challenge: z.string().min(1).max(256),
  response: z.record(z.string(), z.unknown()),
});

/** `POST /agreements/executions/{executionId}/signature` */
export const SignAgreementRequestSchema = z.object({
  challenge: z.string().min(1).max(256),
  contentHash: AgreementSha256Schema,
  signingMethod: z.enum(['drawn', 'uploaded']),
  signerName: z.string().min(1).max(200),
  signerTitle: z.string().max(200).nullable().default(null),
  signerCapacity: z.string().max(200).nullable().default(null),
  signatureObjectKey: z.string().min(1).max(1024),
  signatureMimeType: z.enum(['image/png', 'image/jpeg']),
  signatureByteSize: z.number().int().positive().max(2 * 1024 * 1024),
  /**
   * OPTIONAL contextual evidence, per the documented collection policy. Every field is
   * client-reported and labelled as such; the server records an honest `denied`/`unavailable`
   * location state rather than omitting it. No fingerprinting or biometric telemetry is accepted.
   */
  evidence: z
    .object({
      clientTimezone: z.string().max(64).nullable().default(null),
      locationState: AgreementLocationStateSchema.default('unavailable'),
      locationLatitude: z.number().min(-90).max(90).nullable().default(null),
      locationLongitude: z.number().min(-180).max(180).nullable().default(null),
      locationAccuracyMeters: z.number().nonnegative().nullable().default(null),
      locationSource: z.string().max(64).nullable().default(null),
      locationCapturedAt: IsoDateTimeSchema.nullable().default(null),
    })
    .optional(),
});

/* --------------------------------------------------------------------------------------------
 * Response bodies
 * ------------------------------------------------------------------------------------------ */

export const AgreementAttachmentResponseSchema = z.object({
  attachmentId: UuidSchema,
  ordinal: z.number().int(),
  label: z.string(),
  mimeType: z.string(),
  byteSize: z.number().int(),
  contentHash: AgreementSha256Schema,
});

export const AgreementVersionResponseSchema = z.object({
  versionId: UuidSchema,
  documentId: UuidSchema,
  version: z.number().int(),
  locale: AgreementLocaleSchema,
  isDefaultLocale: z.boolean(),
  title: z.string(),
  content: z.string(),
  contentHash: AgreementSha256Schema,
  effectiveAt: IsoDateTimeSchema,
  publishedAt: IsoDateTimeSchema,
  withdrawnAt: IsoDateTimeSchema.nullable(),
  attachments: z.array(AgreementAttachmentResponseSchema),
});

export const AgreementExecutionResponseSchema = z.object({
  executionId: UuidSchema,
  executionRef: z.string(),
  documentId: UuidSchema,
  versionId: UuidSchema,
  subjectKind: AgreementSubjectKindSchema,
  subjectId: UuidSchema,
  requiredAction: AgreementRequiredActionSchema,
  status: AgreementExecutionStatusSchema,
  signerName: z.string().nullable(),
  signerTitle: z.string().nullable(),
  signerCapacity: z.string().nullable(),
  signingMethod: AgreementSigningMethodSchema.nullable(),
  contentHash: AgreementSha256Schema,
  signatureHash: AgreementSha256Schema.nullable(),
  pdfHash: AgreementSha256Schema.nullable(),
  completedAt: IsoDateTimeSchema.nullable(),
  declinedAt: IsoDateTimeSchema.nullable(),
  expiresAt: IsoDateTimeSchema.nullable(),
  version: z.number().int(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});

export const AgreementExecutionEventResponseSchema = z.object({
  eventId: UuidSchema,
  sequence: z.number().int(),
  eventType: AgreementExecutionEventTypeSchema,
  detail: z.record(z.string(), z.unknown()),
  occurredAt: IsoDateTimeSchema,
});

/**
 * `GET /agreements/executions/{executionId}` — the subject's own execution detail.
 *
 * `downloadUrl` is a short-lived presigned GET URL for the executed PDF, or null until the
 * execution completes. The PDF is immutable: it is rendered once at completion and never
 * regenerated, so the bytes a subject downloads are the bytes that were signed.
 */
export const AgreementExecutionDetailResponseSchema = AgreementExecutionResponseSchema.extend({
  events: z.array(AgreementExecutionEventResponseSchema),
  downloadUrl: z.string().nullable(),
});

/**
 * One row of `GET /agreements/requirements`.
 *
 * This is the ONLY subject-facing list of obligations. There is NO subject-facing
 * list-executions route, so the UI builds signed-PDF history from this list (which carries
 * `executionId`/`executionStatus`) and then reads each execution detail by id.
 */
export const MyAgreementRequirementResponseSchema = z.object({
  requirementId: UuidSchema,
  documentId: UuidSchema,
  versionId: UuidSchema,
  requiredAction: AgreementRequiredActionSchema,
  deadlineAt: IsoDateTimeSchema.nullable(),
  gracePeriodSeconds: z.number().int().nullable(),
  verificationPolicy: SigningVerificationPolicySchema,
  satisfied: z.boolean(),
  executionId: UuidSchema.nullable(),
  executionStatus: AgreementExecutionStatusSchema.nullable(),
});

export const MyAgreementRequirementsResponseSchema = z.object({
  requirements: z.array(MyAgreementRequirementResponseSchema),
});

/** `POST /agreements/executions/{executionId}/signing-sessions` response. */
export const SigningSessionResponseSchema = z.object({
  sessionId: UuidSchema,
  challenge: z.string(),
  intentText: z.string(),
  expiresAt: IsoDateTimeSchema,
  contentHash: AgreementSha256Schema,
  contentManifestHash: AgreementSha256Schema,
  requiredVerificationPolicy: SigningVerificationPolicySchema,
  supportedVerificationMethods: z.array(z.string().min(1).max(64)),
  verificationSatisfied: z.boolean(),
});

/** `POST /agreements/executions/{executionId}/signature-upload` response. */
export const SignatureUploadTargetResponseSchema = z.object({
  sessionId: UuidSchema,
  objectKey: z.string().min(1).max(1024),
  uploadUrl: z.string().min(1).max(4096),
  uploadExpiresAt: IsoDateTimeSchema,
  maxByteSize: z.number().int().positive(),
  allowedMimeTypes: z.array(z.enum(['image/png', 'image/jpeg'])).min(1),
});

/** `POST /agreements/executions/{executionId}/signing-verification` response. */
export const SigningVerificationResponseSchema = z.object({
  verificationId: UuidSchema,
  sessionId: UuidSchema,
  verificationMethod: z.string().min(1).max(64),
  assuranceLevel: z.string().min(1).max(64),
  verifiedAt: IsoDateTimeSchema,
  expiresAt: IsoDateTimeSchema,
});

/**
 * The WebAuthn public-key options the client hands to `navigator.credentials.create`/`get`.
 *
 * The options are the platform's own library output, passed through verbatim. `recoveryPath` is
 * present on the authentication begin response and null on the registration begin response.
 */
export const StepUpChallengeResponseSchema = z.object({
  challengeId: UuidSchema,
  options: z.record(z.string(), z.unknown()),
  recoveryPath: StepUpRecoveryPathSchema.nullable(),
});

export const StepUpRegistrationResponseSchema = z.object({
  credentialId: z.string().min(1).max(1024),
  enrolled: z.literal(true),
});

export const StepUpAuthenticationResponseSchema = z.object({
  stepUpId: UuidSchema,
  credentialId: z.string().min(1).max(1024),
  verifiedAt: IsoDateTimeSchema,
  expiresAt: IsoDateTimeSchema,
  /** Always `aal2` — a server-recorded WebAuthn step-up. Never a claim of a digital signature. */
  assuranceLevel: z.literal('aal2'),
  requiredVerificationPolicy: SigningVerificationPolicySchema,
});

/* --------------------------------------------------------------------------------------------
 * Inferred types live in `agreements.types.ts` to keep this file within the module's
 * `max-lines` budget. Import them from there.
 * ------------------------------------------------------------------------------------------ */

